import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as deviceApi from '../services/deviceApi';
import { useAuth } from '../contexts/AuthContext';
import {
    PlusCircle,
    Edit,
    Trash,
    AlertCircle,
    CheckCircle,
    MapPin,
    QrCode as QrCodeIcon,
    Server,
    Cpu,
} from 'lucide-react';
import DeviceForm from '../components/DeviceForm';
import PanelForm from '../components/PanelForm';
import PanelEditForm from '../components/PanelEditForm';
import DataTable from '../components/common/DataTable';
import InfoModal from '../components/common/InfoModal';
import QRCodeModal from '../components/QRCodeModal';
import { BRAND_LABELS } from '../components/deviceCatalog';

const DevicesPage = () => {
    const { isAuthenticated, permissions = [], hasPermission } = useAuth();

    const [devices, setDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingDevice, setEditingDevice] = useState(null);

    const [isPanelModalOpen, setIsPanelModalOpen] = useState(false);
    const [isPanelEditModalOpen, setIsPanelEditModalOpen] = useState(false);
    const [editingPanel, setEditingPanel] = useState(null);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [deviceToDeactivateId, setDeviceToDeactivateId] = useState(null);

    const [showQrModal, setShowQrModal] = useState(false);
    const [selectedQrData, setSelectedQrData] = useState(null);
    const [selectedDeviceName, setSelectedDeviceName] = useState(null);

    const canView = useMemo(
        () => (typeof hasPermission === 'function' ? hasPermission('devices:view_list') : permissions.includes('devices:view_list')),
        [hasPermission, permissions]
    );
    const canCreate = useMemo(
        () => (typeof hasPermission === 'function' ? hasPermission('devices:create') : permissions.includes('devices:create')),
        [hasPermission, permissions]
    );
    const canUpdate = useMemo(
        () => (typeof hasPermission === 'function' ? hasPermission('devices:update') : permissions.includes('devices:update')),
        [hasPermission, permissions]
    );

    const fetchDevices = useCallback(async () => {
        if (!canView) {
            setError('Cihazları listeleme yetkiniz bulunmamaktadır.');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const data = await deviceApi.getAllDevices();
            setDevices(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError('Cihazlar yüklenemedi. Lütfen tekrar deneyin.');
        } finally {
            setIsLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchDevices();
        }
    }, [isAuthenticated, fetchDevices]);

    const handleCloseModals = () => {
        setIsFormModalOpen(false);
        setEditingDevice(null);
        setIsPanelModalOpen(false);
        setIsPanelEditModalOpen(false);
        setEditingPanel(null);
        setIsConfirmModalOpen(false);
        setDeviceToDeactivateId(null);
        setShowQrModal(false);
        setSelectedQrData(null);
        setSelectedDeviceName(null);
    };

    const handleOpenCreateModal = () => {
        setMessage(null);
        setError(null);
        setEditingDevice(null);
        setIsFormModalOpen(true);
    };

    const handleOpenPanelModal = () => {
        setMessage(null);
        setError(null);
        setIsPanelModalOpen(true);
    };

    const handleOpenEditModal = useCallback(async (device) => {
        setMessage(null);
        setError(null);
        if (device.isPanel) {
            try {
                const panelDetails = await deviceApi.getPanelById(device.id);
                setEditingPanel(panelDetails);
                setIsPanelEditModalOpen(true);
            } catch (err) {
                console.error(err);
                setError('Panel detayları yüklenirken bir hata oluştu.');
            }
        } else {
            setEditingDevice(device);
            setIsFormModalOpen(true);
        }
    }, []);

    const handleShowQrCode = useCallback((device) => {
        setSelectedQrData(device.serialNumber);
        setSelectedDeviceName(device.deviceName);
        setShowQrModal(true);
    }, []);

    const handleDeactivateRequest = useCallback((id) => {
        setDeviceToDeactivateId(id);
        setIsConfirmModalOpen(true);
    }, []);

    const handleDeactivateConfirm = async () => {
        if (!canUpdate || !deviceToDeactivateId) {
            setIsConfirmModalOpen(false);
            return;
        }
        try {
            setIsLoading(true);
            await deviceApi.deactivateDevice(deviceToDeactivateId);
            setMessage('Cihaz başarıyla pasif hale getirildi.');
            handleCloseModals();
            await fetchDevices();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || 'Cihaz pasif hale getirilemedi.');
            setIsConfirmModalOpen(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveDevice = async (deviceData) => {
        if (editingDevice?.id || deviceData?.id) {
            const targetId = editingDevice?.id || deviceData.id;
            await deviceApi.updateDevice(targetId, deviceData);
            setMessage('Cihaz başarıyla güncellendi.');
        } else {
            await deviceApi.createDevice(deviceData);
            setMessage('Cihaz başarıyla eklendi.');
        }
        handleCloseModals();
        await fetchDevices();
    };

    const handleSavePanel = async (panelPayload) => {
        await deviceApi.createPanel(panelPayload);
        setMessage('Panel cihazı başarıyla eklendi.');
        handleCloseModals();
        await fetchDevices();
    };

    const handleUpdatePanel = async (id, panelPayload) => {
        await deviceApi.updatePanel(id, panelPayload);
        setMessage('Panel cihazı başarıyla güncellendi.');
        handleCloseModals();
        await fetchDevices();
    };

    const deviceColumns = useMemo(
        () => [
            {
                header: 'Cihaz / Panel Adı',
                accessor: 'deviceName',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div className="flex items-center gap-2.5">
                        <div
                            className={`p-2 rounded-xl ${
                                row.isPanel
                                    ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300'
                                    : row.isQrDevice
                                    ? 'bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                        >
                            {row.isPanel ? (
                                <Server className="w-4 h-4" />
                            ) : row.isQrDevice ? (
                                <QrCodeIcon className="w-4 h-4" />
                            ) : (
                                <Cpu className="w-4 h-4" />
                            )}
                        </div>
                        <div>
                            <div className="font-semibold text-gray-900 dark:text-gray-100">{row.deviceName}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {row.isPanel
                                    ? 'Çoklu Okuyucu Paneli (1..4 Slot)'
                                    : row.isQrDevice
                                    ? row.isLocationBased
                                        ? 'Konumlu Mobil QR'
                                        : 'Sabit Mobil QR'
                                    : 'Terminal Cihaz'}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                header: 'Marka / Model',
                accessor: 'brand',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                            {BRAND_LABELS[row.brand] || row.brand || '-'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{row.model || '-'}</div>
                    </div>
                ),
            },
            {
                header: 'Konum / Gateway',
                accessor: 'location',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div>
                        <div className="text-sm text-gray-800 dark:text-gray-200">{row.location || '-'}</div>
                        {row.sourceName && (
                            <span className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-700/50">
                                GW: {row.sourceName}
                            </span>
                        )}
                    </div>
                ),
            },
            {
                header: 'Seri No / MAC',
                accessor: 'serialNumber',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div>
                        <div className="font-mono text-xs font-medium text-gray-900 dark:text-gray-100">
                            {row.serialNumber || '-'}
                        </div>
                        {row.macAddress && row.macAddress !== row.serialNumber && (
                            <div className="font-mono text-[11px] text-gray-500 dark:text-gray-400">
                                MAC: {row.macAddress}
                            </div>
                        )}
                    </div>
                ),
            },
            {
                header: 'Ağ Bilgisi',
                accessor: 'ipAddress',
                sortable: true,
                searchable: true,
                render: (row) => {
                    if (row.isQrDevice) {
                        return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>;
                    }
                    return (
                        <div className="text-xs">
                            <div className="font-mono text-gray-800 dark:text-gray-200">
                                {row.ipAddress ? `${row.ipAddress}:${row.port || ''}` : '-'}
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                {row.protocol || 'ISAPI'}
                                {row.useHttps ? ' • HTTPS' : ''}
                            </div>
                        </div>
                    );
                },
            },
            {
                header: 'Yön',
                accessor: 'direction',
                sortable: true,
                searchable: false,
                render: (row) => {
                    let bgColor = 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
                    let label = 'Belirtilmemiş';

                    if (row.isPanel) {
                        bgColor = 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200';
                        label = 'Panel (Slot Bazlı)';
                    } else if (row.isEntryDevice && row.isExitDevice) {
                        bgColor = 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200';
                        label = 'Giriş / Çıkış';
                    } else if (row.isEntryDevice) {
                        bgColor = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200';
                        label = 'Giriş';
                    } else if (row.isExitDevice) {
                        bgColor = 'bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200';
                        label = 'Çıkış';
                    }

                    return (
                        <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor}`}>
                            {label}
                        </span>
                    );
                },
            },
            {
                header: 'Durum',
                accessor: 'isActive',
                sortable: true,
                searchable: false,
                render: (row) => {
                    const bgColor = row.isActive
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200'
                        : 'bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200';
                    return (
                        <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor}`}>
                            {row.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                    );
                },
            },
            {
                header: 'İşlemler',
                accessor: 'actions',
                sortable: false,
                searchable: false,
                render: (row) => (
                    <div className="flex justify-center items-center space-x-1">
                        {canUpdate && (
                            <button
                                onClick={() => handleOpenEditModal(row)}
                                className="p-1.5 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors"
                                title="Düzenle"
                            >
                                <Edit className="w-4 h-4" />
                            </button>
                        )}
                        {row.isQrDevice && (
                            <button
                                onClick={() => handleShowQrCode(row)}
                                className="p-1.5 rounded-lg text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-gray-700 transition-colors"
                                title="QR Kodu Görüntüle"
                            >
                                <QrCodeIcon className="w-4 h-4" />
                            </button>
                        )}
                        {row.isLocationBased && (
                            <span
                                className="p-1.5 rounded-lg text-amber-500"
                                title={`Konum Bazlı (${row.latitude || ''}, ${row.longitude || ''})`}
                            >
                                <MapPin className="w-4 h-4" />
                            </span>
                        )}
                        {row.isActive && canUpdate && (
                            <button
                                onClick={() => handleDeactivateRequest(row.id)}
                                className="p-1.5 rounded-lg text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-gray-700 transition-colors"
                                title="Pasif Yap"
                            >
                                <Trash className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ),
            },
        ],
        [canUpdate, handleOpenEditModal, handleShowQrCode, handleDeactivateRequest]
    );

    if (!canView && !isLoading) {
        return <div className="p-4 text-red-600 dark:text-red-400">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</div>;
    }

    const actionsHeader = canCreate ? (
        <div className="flex flex-wrap items-center gap-3">
            <button
                type="button"
                onClick={handleOpenCreateModal}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-all flex items-center gap-2"
            >
                <PlusCircle className="w-4 h-4" />
                Yeni Cihaz Ekle
            </button>
            <button
                type="button"
                onClick={handleOpenPanelModal}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-all flex items-center gap-2"
            >
                <Server className="w-4 h-4" />
                Yeni Panel Ekle (4 Okuyuculu)
            </button>
        </div>
    ) : null;

    return (
        <div className="p-1 space-y-4">
            {message && (
                <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 px-4 py-3 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                        <span className="text-sm font-medium">{message}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setMessage(null)}
                        className="text-xs font-semibold hover:underline"
                    >
                        Kapat
                    </button>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-200 px-4 py-3 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-rose-500" />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setError(null)}
                        className="text-xs font-semibold hover:underline"
                    >
                        Kapat
                    </button>
                </div>
            )}

            <DataTable
                data={devices}
                columns={deviceColumns}
                isLoading={isLoading}
                actionsHeader={actionsHeader}
                searchPlaceholder="Cihaz adı, seri no, marka veya lokasyon ara..."
                emptyMessage="Kayıtlı cihaz veya panel bulunamadı."
            />

            {/* Cihaz Ekle / Düzenle Modalı */}
            <InfoModal
                isOpen={isFormModalOpen}
                onClose={handleCloseModals}
                title={editingDevice ? 'Cihazı Düzenle' : 'Yeni Cihaz Ekle'}
                showFooter={false}
                className="max-w-6xl"
            >
                <DeviceForm
                    device={editingDevice}
                    onSave={handleSaveDevice}
                    onSubmit={handleSaveDevice}
                    onCancel={handleCloseModals}
                />
            </InfoModal>

            {/* Yeni Panel Ekle Modalı */}
            <InfoModal
                isOpen={isPanelModalOpen}
                onClose={handleCloseModals}
                title="Yeni Panel Cihaz Ekle (Maksimum 4 Okuyuculu)"
                showFooter={false}
                className="max-w-6xl"
            >
                <PanelForm
                    onSave={handleSavePanel}
                    onSubmit={handleSavePanel}
                    onCancel={handleCloseModals}
                />
            </InfoModal>

            {/* Panel Düzenle Modalı */}
            <InfoModal
                isOpen={isPanelEditModalOpen}
                onClose={handleCloseModals}
                title="Panel Cihazı Düzenle (Maksimum 4 Okuyuculu)"
                showFooter={false}
                className="max-w-6xl"
            >
                <PanelEditForm
                    panelData={editingPanel}
                    onSave={handleUpdatePanel}
                    onCancel={handleCloseModals}
                />
            </InfoModal>

            {/* Cihaz Pasife Alma Onay Modalı */}
            <InfoModal
                isOpen={isConfirmModalOpen}
                onClose={handleCloseModals}
                title="Cihazı Pasif Yap"
                message="Bu cihazı pasif hale getirmek istediğinizden emin misiniz?"
                type="confirm"
                onConfirm={handleDeactivateConfirm}
            />

            {/* QR Kod Modalı */}
            {showQrModal && (
                <QRCodeModal
                    qrData={selectedQrData}
                    deviceName={selectedDeviceName}
                    onClose={handleCloseModals}
                />
            )}
        </div>
    );
};

export default DevicesPage;
